import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil, switchMap } from "rxjs/operators";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCommonModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { ActivatedRoute, Router } from "@angular/router";
import { tap } from "rxjs/operators";
import { FinancialInformationService } from "src/app/iap/services/financial-information.service";
import { MyProfileService } from "src/app/iap/services/my-profile.service";
import { environment } from "src/environments/environment";

@Component({
    selector: 'app-direct-deposit',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatCommonModule,
    ],
    templateUrl: './direct-deposit.component.html',
    styleUrl: './direct-deposit.component.scss'
})
export class DirectDepositComponent implements OnInit, OnDestroy {
  @Input() agencyId: string;
  @Input() paymentMethod: string = '';
  @Output() submitted = new EventEmitter<void>();
  DDForm!: FormGroup;
  financialInfoData: any;
  isSubmitting = false;
  isSubmitted = false;
  showFillTestDataButton: boolean = false; //show button only in dev mode
  private destroy$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private financialInformationService: FinancialInformationService, private myProfileService: MyProfileService) {
  }

  ngOnInit(): void {
    this.showFillTestDataButton = environment.showTestButton;

    if (this.agencyId) {
      this.DDForm = this.createFormGroup();
      // Set Payment_Method from parent radio selection and lock it
      this.DDForm.get('Payment_Method')?.setValue(this.paymentMethod);
      this.DDForm.get('Payment_Method')?.disable();
    } else {
      console.error("Agency ID is not set.");
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToAgencyList(): void {
    // this.router.navigateByUrl("/agency/agencies");
    console.log("goToAgencyList clicked");
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.DDForm.invalid) {
      this.DDForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    console.log("agencyId: ", this.agencyId);
    console.log(this.DDForm.value);

    this.financialInformationService.submitFinancialInfo(this.DDForm.getRawValue())
      .pipe(
        switchMap((res) => {
          console.log("response: ", res);
          if (res.status === 200) {
            console.log("Successfully submitted financial information");
            return this.myProfileService.updateAgencyOnboardingStatus(
              Number(this.agencyId),
              "true",
              "step2"
            );
          }
          throw new Error("API response status was not 200");
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (result) => {
          console.log("updated onboarding status!: ", result);
          this.isSubmitting = false;
          this.isSubmitted = true;
          this.submitted.emit();
        },
        (error) => {
          console.error("Error during submission:", error);
          this.isSubmitting = false;
        }
      );
  }

  private createFormGroup(): FormGroup {
    return this.formBuilder.group({
      Agency_ID: [this.agencyId],
      Payment_Method: ["", Validators.required],
      BF_Bank_Name: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
      BF_Bank_Address: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
      BF_Bank_Phone: ["", [Validators.required, Validators.pattern(/^\+?[0-9\s\-().]{7,30}$/)]],
      BF_Bank_Account: ["", [Validators.required, Validators.pattern(/^[0-9A-Za-z\-]+$/)]],
      BF_Bank_Code: ["", [Validators.required, Validators.pattern(/^[0-9A-Za-z\-]+$/)]],
      BF_Bank_Transit: ["", [Validators.required, Validators.pattern(/^[0-9A-Za-z\-]+$/)]],
      BF_Bank_ABA: [""],
      BF_Bank_Swift: [""],
      BF_Bank_IBAN: [""],
      IN_Bank_Name: [""],
      IN_Bank_Address: [""],
      IN_Bank_Account: [""],
      IN_Bank_Swift: [""],
      DD_Legal_Name: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
      DD_HST_GST_Number: [""],             // optional
      DD_SIN: [""],                         // optional
      CP_Last_Name: ["", [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/)]],
      CP_First_Name: ["", [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/)]],
      CP_Bank_Phone: ["", [Validators.required, Validators.pattern(/^\+?[0-9\s\-().]{7,30}$/)]],
      CP_Email: ["", [Validators.required, Validators.email]],
      AS_Last_Name: ["", [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/)]],
      AS_First_Name: ["", [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/)]],
      AS_Title: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/), Validators.maxLength(10)]],
      AS_Address: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
      AS_Phone: ["", [Validators.required, Validators.pattern(/^\+?[0-9\s\-().]{7,30}$/)]],
      AS_Fax: [""],
    });
  }

  fillTestData(): void {
    this.DDForm.patchValue({
      Agency_ID: this.agencyId,
    //  Payment_Method: "D",
    //  Currency: "CAD",
   //   BF_Name: "ABC Education Agency Inc.",
      BF_Address: "123 Education Street, Toronto, ON M5V 2T6",
      BF_Bank_Name: "Royal Bank of Canada",
      BF_Bank_Address: "200 Bay Street, Toronto, ON M5J 2J5",
      BF_Bank_Phone: "4169740000",
      BF_Bank_Account: "12345678",
      BF_Bank_Code: "003",
      BF_Bank_Transit: "12345",
      BF_Bank_ABA: "",
      BF_Bank_Swift: "",
      BF_Bank_IBAN: "",
      IN_Bank_Name: "",
      IN_Bank_Address: "",
      IN_Bank_Account: "",
      IN_Bank_Swift: "",
      DD_Legal_Name: "ABC Education Agency Inc.",
      DD_HST_GST_Number: "123456789RT0001",
      DD_SIN: "123456789",
      CP_Last_Name: "Johnson",
      CP_First_Name: "Robert",
      CP_Bank_Phone: "4165550123",
      CP_Email: "robert.johnson@abceducation.com",
      AS_Last_Name: "Johnson",
      AS_First_Name: "Robert",
      AS_Title: "Director",
      AS_Address: "123 Education Street, Toronto, ON M5V 2T6",
      AS_Phone: "4165550123",
      AS_Fax: "4165550124"
    });
  }
}
